import { useEffect, useState } from "react";
import { Calculator, FileCheck, ShieldCheck, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { useAuth, STAFF_ROLES } from "../../context/AuthContext";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, Money, StatusBadge, Modal, Field, ErrBox, Table, Empty } from "../../components/common";

const FILING_METHODS = ["Electronic API", "Government Portal", "Manual", "Third-Party Professional", "Paper Filing"];

export default function Filings() {
  const { user, business } = useAuth();
  const [periods, setPeriods] = useState(null);
  const [steps, setSteps] = useState([]);
  const [review, setReview] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [method, setMethod] = useState("Government Portal");
  const [confirmation, setConfirmation] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isStaff = user && STAFF_ROLES.includes(user.role);

  const load = () => API.get("/periods").then((r) => { setPeriods(r.data.periods); setSteps(r.data.workflow_steps); });
  useEffect(() => { load(); }, []);

  const openReview = async (p) => {
    const { data } = await API.get(`/periods/${p.id}/review`);
    setReview(data);
    setErr(""); setAuthChecked(false); setConfirmation(""); setMethod("Government Portal");
  };

  const calculate = async () => {
    setBusy(true); setErr("");
    try {
      await API.post(`/periods/${review.period.id}/calculate`);
      toast.success("Tax recalculated and versioned");
      const { data } = await API.get(`/periods/${review.period.id}/review`);
      setReview(data); load();
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const setStatus = async (status, extra = {}) => {
    setBusy(true); setErr("");
    try {
      await API.post(`/periods/${review.period.id}/status`, { status, ...extra });
      toast.success(`Status updated: ${status.replace(/_/g, " ")}`);
      const { data } = await API.get(`/periods/${review.period.id}/review`);
      setReview(data); load(); setFileOpen(false);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const authorize = async () => {
    setBusy(true); setErr("");
    try {
      await API.post(`/periods/${review.period.id}/authorize`, {
        amount: review.period.balance_cents / 100, payment_account: "Business operating account", terms_version: "1.0",
      });
      toast.success("Filing and payment authorized");
      setAuthOpen(false);
      const { data } = await API.get(`/periods/${review.period.id}/review`);
      setReview(data); load();
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  if (!periods) return <Loading label="Loading filings" />;
  const p = review?.period;

  return (
    <div data-testid="filings-page">
      <PageHeader title="Tax Periods & Filing Workflow" sub="Every period from first sale to filed, paid, and proven." />

      {periods.length === 0 ? <Empty title="No tax periods yet" /> : (
        <Table headers={["Period", "Tax Type", "Gross", "Taxable", "Liability", "Balance Due", "Due Date", "Status", ""]} testid="periods-table">
          {periods.map((per) => (
            <tr key={per.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => openReview(per)} data-testid={`period-row-${per.label.replace(/ /g, "-")}`}>
              <td className="px-4 py-3 font-semibold text-navy">{per.label}</td>
              <td className="px-4 py-3">{per.tax_type}</td>
              <td className="px-4 py-3 money"><Money cents={per.gross_cents} /></td>
              <td className="px-4 py-3 money"><Money cents={per.taxable_cents} /></td>
              <td className="px-4 py-3 money font-semibold"><Money cents={per.liability_cents} /></td>
              <td className={`px-4 py-3 money font-semibold ${per.balance_cents > 0 ? "text-rose-600" : "text-emerald-700"}`}><Money cents={per.balance_cents} /></td>
              <td className="px-4 py-3">{fmtDate(per.due_date)}</td>
              <td className="px-4 py-3"><StatusBadge status={per.status} /></td>
              <td className="px-4 py-3 text-copper"><FileCheck className="h-4 w-4" /></td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={!!review} onClose={() => setReview(null)} title={p ? `Tax Return Review — ${p.label}` : ""} wide>
        {p && (
          <div className="space-y-5" data-testid="period-review">
            <div className="flex flex-wrap items-center gap-1.5">
              {steps.map((s, i) => (
                <div key={s} className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${i <= review.workflow_step ? "border-navy bg-navy text-white" : "border-slate-200 text-slate-400"}`}>
                  {i + 1}. {s}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Reported Sales", p.gross_cents], ["Taxable Sales", p.taxable_cents], ["Deductions", p.deductions_cents],
                ["Calculated Tax", p.liability_cents], ["Penalties", p.penalties_cents], ["Interest", p.interest_cents],
                ["Credits", p.credits_cents], ["Payments", p.payments_cents]].map(([k, v]) => (
                <div key={k} className="rounded border border-slate-200 px-3 py-2">
                  <dt className="text-[10px] uppercase tracking-widest text-slate-400">{k}</dt>
                  <dd className="money font-bold text-slate-800"><Money cents={v} /></dd>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-md border-2 border-navy bg-navy-50 px-4 py-3">
              <span className="font-bold text-navy">Amount Due</span>
              <Money cents={p.balance_cents} exact className="text-2xl font-bold text-navy" />
            </div>

            <div className="grid gap-2 text-xs text-slate-500 sm:grid-cols-3">
              <div>Jurisdiction: <span className="font-semibold text-slate-700">{review.calculations[0]?.jurisdiction || "Arizona"}</span></div>
              <div>Rate: <span className="font-semibold text-slate-700">{review.calculations[0]?.rate_percent || "—"}%</span></div>
              <div>Calc version: <span className="font-semibold text-slate-700">v{review.calculations[0]?.version || 0} · rule v{review.calculations[0]?.rule_version || "—"}</span></div>
            </div>

            {p.filing_method && (
              <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                Filed via <strong>{p.filing_method}</strong>{p.confirmation_number ? <> · Confirmation <span className="font-mono font-semibold">{p.confirmation_number}</span></> : ""}
              </div>
            )}

            {review.authorizations.length > 0 && (
              <div className="text-xs text-slate-500">
                Authorization on record: {review.authorizations[0].user_email} · {fmtDate(review.authorizations[0].created_at)} · <Money cents={review.authorizations[0].amount_cents} exact /> authorized
              </div>
            )}

            <ErrBox msg={err} />
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
              <button className="btn-outline-gov" onClick={calculate} disabled={busy} data-testid="recalculate-btn"><Calculator className="h-4 w-4" /> Recalculate</button>
              {isStaff && ["IN_PROGRESS", "REVIEW_REQUIRED", "PAST_DUE"].includes(p.status) && (
                <button className="btn-navy" onClick={() => setStatus("READY_TO_FILE")} disabled={busy} data-testid="mark-ready-btn">Approve — Ready to File</button>
              )}
              {!isStaff && ["READY_TO_FILE", "IN_PROGRESS", "REVIEW_REQUIRED"].includes(p.status) && (
                <button className="btn-copper" onClick={() => setAuthOpen(true)} disabled={busy} data-testid="authorize-btn"><ShieldCheck className="h-4 w-4" /> Authorize Filing & Payment</button>
              )}
              {isStaff && ["READY_TO_FILE", "AWAITING_CLIENT_APPROVAL"].includes(p.status) && (
                <button className="btn-navy" onClick={() => setFileOpen(true)} disabled={busy} data-testid="mark-filed-btn"><Upload className="h-4 w-4" /> Mark Filed</button>
              )}
              {isStaff && p.status === "FILED" && (
                <button className="btn-navy" onClick={() => setStatus("PAYMENT_PENDING")} disabled={busy} data-testid="payment-pending-btn">Move to Payment Pending</button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={authOpen} onClose={() => setAuthOpen(false)} title="Authorize Filing & Payment">
        {p && (
          <div className="space-y-4" data-testid="authorization-modal">
            <dl className="grid grid-cols-2 gap-2 text-sm">
              {[["Tax Period", p.label], ["Tax Form", "Arizona TPT Return (TPT-2)"],
                ["Tax Amount", null], ["Payment Date", fmtDate(p.due_date)]].map(([k, v]) => (
                <div key={k} className="rounded border border-slate-200 px-3 py-2">
                  <dt className="text-[10px] uppercase tracking-widest text-slate-400">{k}</dt>
                  <dd className="font-semibold text-slate-800">{k === "Tax Amount" ? <Money cents={p.balance_cents} exact /> : v}</dd>
                </div>
              ))}
            </dl>
            <p className="rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
              I authorize AZ Tax Accounts to file the return shown above and to initiate payment of the authorized amount
              on or around the payment date. This authorization (text version 1.0) is recorded with my identity, timestamp,
              and session. If the amount changes materially, I will be asked to re-authorize.
            </p>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={authChecked} onChange={(e) => setAuthChecked(e.target.checked)} data-testid="auth-terms-check" />
              I have reviewed the amounts above and agree to the <span className="font-semibold">Tax Filing Authorization</span> and <span className="font-semibold">Payment Authorization</span> terms.
            </label>
            <ErrBox msg={err} />
            <button className="btn-copper w-full py-3" disabled={!authChecked || busy} onClick={authorize} data-testid="auth-confirm-btn">
              {busy ? "Recording authorization…" : "Authorize Filing and Payment"}
            </button>
          </div>
        )}
      </Modal>

      <Modal open={fileOpen} onClose={() => setFileOpen(false)} title="Mark Return as Filed">
        <div className="space-y-4" data-testid="mark-filed-modal">
          <p className="text-sm text-slate-600">Only mark a return filed when it has actually been submitted. The filing method is recorded for the audit trail — never claim an electronic API filing that didn't happen.</p>
          <Field label="Filing Method *">
            <select className="input-gov" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="filing-method">
              {FILING_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Confirmation Number"><input className="input-gov font-mono" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="filing-confirmation" placeholder="e.g. ADOR-202605-88412" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} onClick={() => setStatus("FILED", { filing_method: method, confirmation_number: confirmation })} data-testid="confirm-filed-btn">
            <CheckCircle2 className="h-4 w-4" /> {busy ? "Saving…" : "Confirm Filed"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
